import clsx from "clsx";
import AppStoreBlack from "img/ui/app-store-black.svg?inline";
import PlayStoreBlack from "img/ui/play-store-black.svg?inline";
import React from "react";
import { useDispatch } from "react-redux";
import AdjustLink from "web/react/components/adjust-link/adjust-link";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import { getAdjustCampaignName, RawAdjustLink } from "web/script/utils/adjust";
import * as styles from "./app-download-badge.css";

type AppDownloadLink = "footer" | "appTeaser";

const APP_DOWNLOAD_LINKS: {
    readonly [lnk in AppDownloadLink]: RawAdjustLink;
} = {
    footer: RawAdjustLink.Footer,
    appTeaser: RawAdjustLink.AppTeaser,
};

interface AppDownloadButtonProps {
    appDownloadLink?: AppDownloadLink;
    eventLabel: string;
    buttonText?: string;
    badgeType?: "android" | "ios";
}

export function AppDownloadBadge({
    appDownloadLink = "appTeaser",
    eventLabel,
    buttonText = "",
    badgeType = "ios",
}: AppDownloadButtonProps): JSX.Element {
    const adjustLink = APP_DOWNLOAD_LINKS[appDownloadLink];
    const svgProps = { height: "80%", width: "100%", viewBox: "0 0 120 40" };

    const dispatch = useDispatch();
    const { isDesktopViewport } = useDomViewport();

    const trackedClick = useClickTracker("web_to_app", "click", eventLabel, false, {
        campaign_name: getAdjustCampaignName(adjustLink),
    });

    function scanQrCode(e): void {
        e.preventDefault();
        trackedClick(e);
        dispatch(
            openCustomerCaptureOverlay({
                captureType: "qr_code",
                adjustLink,
            })
        );
    }

    function appBadge(): JSX.Element {
        return badgeType === "android" ? (
            <PlayStoreBlack {...svgProps} viewBox={"0 0 135 40"} />
        ) : (
            <AppStoreBlack {...svgProps} />
        );
    }

    const className = clsx(styles.badge, styles.svg[badgeType], styles.commonSvg);

    if (isDesktopViewport) {
        return (
            <button
                type="button"
                className={className}
                onClick={scanQrCode}
                aria-label={buttonText}
                title={buttonText}
            >
                <span>{appBadge()}</span>
            </button>
        );
    }

    return (
        <AdjustLink
            className={clsx(className)}
            adjustLink={adjustLink}
            eventLabel={eventLabel}
            title={buttonText}
        >
            <span>{appBadge()}</span>
        </AdjustLink>
    );
}

export default AppDownloadBadge;
